import { ORANGE_ACTION } from '@cubemeet/ui';

export const ORANGE = ORANGE_ACTION;

export const coalesceText = (...values: Array<string | null | undefined>): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.length > 0) return trimmed;
    }
  }
  return undefined;
};

export const arrayify = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        if (item && typeof item === 'object') {
          try { return JSON.stringify(item); } catch { return ''; }
        }
        return '';
      })
      .filter((item) => item.length > 0);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }
  if (value && typeof value === 'object') {
    try { return [JSON.stringify(value)]; } catch { return []; }
  }
  return [];
};

export const formatList = (value: unknown): string | undefined => {
  const list = arrayify(value);
  return list.length ? list.join('、') : undefined;
};

export const normalizeExternalLink = (value?: string | null): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^\/\//.test(trimmed)) return `https:${trimmed}`;
  return `https://${trimmed}`;
};

export const formatDuration = (minutes?: number | null): string | undefined => {
  if (!minutes || minutes <= 0) return undefined;
  if (minutes < 60) return `${minutes} 分钟`;
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins ? `${hrs} 小时 ${mins} 分钟` : `${hrs} 小时`;
};

export const formatOpeningHours = (value: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') {
    return value.trim() || undefined;
  }
  if (Array.isArray(value)) {
    const lines = value
      .map((entry) => {
        if (!entry) return null;
        if (typeof entry === 'string') return entry.trim();
        if (typeof entry === 'object') {
          const day = coalesceText((entry as any).day, (entry as any).label);
          const open = coalesceText((entry as any).open, (entry as any).start);
          const close = coalesceText((entry as any).close, (entry as any).end);
          if (day && open && close) return `${day} ${open}-${close}`;
          if (day && open) return `${day} ${open}`;
          if (open && close) return `${open}-${close}`;
          if (day) return day;
        }
        return null;
      })
      .filter((line): line is string => Boolean(line && line.trim()));
    return lines.length ? lines.join('\n') : undefined;
  }
  if (typeof value === 'object') {
    const day = coalesceText((value as any).day, (value as any).label);
    const open = coalesceText((value as any).open, (value as any).start);
    const close = coalesceText((value as any).close, (value as any).end);
    if (day && open && close) return `${day} ${open}-${close}`;
    if (open && close) return `${open}-${close}`;
    if (day && open) return `${day} ${open}`;
    if (day) return day;
  }
  return undefined;
};

export const isPlainObject = (value: unknown): value is Record<string, any> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

export const mergePayload = (it: any) => {
  const cardPayload = isPlainObject(it?.card?.base_payload) ? it.card.base_payload : {};
  const itemPayload = isPlainObject(it?.payload) ? it.payload : {};
  const inlinePayload = isPlainObject(it?.inline_payload) ? it.inline_payload : {};
  const customPayload = isPlainObject(it?.custom_payload) ? it.custom_payload : {};
  return { ...cardPayload, ...itemPayload, ...inlinePayload, ...customPayload } as Record<string, any>;
};

export interface MapRouteModeMeta {
  key: string;
  label: string;
  shortLabel?: string;
  icon: string;
}

const BASE_MAP_ROUTE_MODES: Record<string, MapRouteModeMeta> = {
  driving: { key: 'driving', label: '自驾路线', shortLabel: '自驾', icon: 'directions-car' },
  two_wheeler: { key: 'two_wheeler', label: '机车路线', shortLabel: '机车', icon: 'two_wheeler' },
  transit: { key: 'transit', label: '大众运输', shortLabel: '大众运输', icon: 'directions-bus' },
  walking: { key: 'walking', label: '步行路线', shortLabel: '步行', icon: 'directions-walk' },
  bicycling: { key: 'bicycling', label: '骑行路线', shortLabel: '骑行', icon: 'directions-bike' },
};

const MAP_ROUTE_MODE_ALIAS: Record<string, keyof typeof BASE_MAP_ROUTE_MODES> = {
  drive: 'driving',
  car: 'driving',
  driving: 'driving',
  automobile: 'driving',
  'two-wheeler': 'two_wheeler',
  two_wheeler: 'two_wheeler',
  motorcycle: 'two_wheeler',
  scooter: 'two_wheeler',
  transit: 'transit',
  bus: 'transit',
  public: 'transit',
  subway: 'transit',
  metro: 'transit',
  train: 'transit',
  walk: 'walking',
  walking: 'walking',
  pedestrian: 'walking',
  bike: 'bicycling',
  biking: 'bicycling',
  bicycling: 'bicycling',
  cycle: 'bicycling',
  cycling: 'bicycling',
};

export const listMapRouteModes = (): MapRouteModeMeta[] => Object.values(BASE_MAP_ROUTE_MODES);

export const resolveMapRouteModeMeta = (mode?: string | null): MapRouteModeMeta | undefined => {
  if (!mode) return undefined;
  const normalized = String(mode).trim().toLowerCase();
  if (!normalized) return undefined;
  const key = MAP_ROUTE_MODE_ALIAS[normalized] || (normalized in BASE_MAP_ROUTE_MODES ? (normalized as keyof typeof BASE_MAP_ROUTE_MODES) : undefined);
  if (!key) return undefined;
  return BASE_MAP_ROUTE_MODES[key];
};

const pickFirstImage = (value: any): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    if (trimmed.startsWith('//')) return `https:${trimmed}`;
    return trimmed;
  }
  if (Array.isArray(value)) {
    for (const entry of value) {
      const candidate = pickFirstImage(entry);
      if (candidate) return candidate;
    }
    return undefined;
  }
  if (typeof value === 'object') {
    const prioritizedKeys = ['url', 'uri', 'src', 'cover', 'image', 'imageUrl', 'image_url', 'photo', 'thumbnail'];
    for (const key of prioritizedKeys) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        const candidate = pickFirstImage((value as any)[key]);
        if (candidate) return candidate;
      }
    }
  }
  return undefined;
};

export const deriveImageUrl = (it: any, mergedPayloadInput?: Record<string, any>): string | undefined => {
  const mergedPayload = mergedPayloadInput || mergePayload(it) || {};
  const directCandidates = [
    it?.coverImage,
    it?.cover_image,
    it?.imageUrl,
    it?.image_url,
    it?.meta?.coverImage,
    it?.meta?.cover_image,
    it?.card?.cover_image,
    it?.card?.coverImage,
    it?.card?.image,
    it?.card?.imageUrl,
    it?.card?.image_url,
    it?.card?.photo,
    it?.card?.thumbnail,
    mergedPayload.cover_image,
    mergedPayload.coverImage,
    mergedPayload.image,
    mergedPayload.imageUrl,
    mergedPayload.image_url,
    mergedPayload.photo,
    mergedPayload.thumbnail,
  ];

  for (const candidate of directCandidates) {
    const url = pickFirstImage(candidate);
    if (url) return url;
  }

  const galleryCandidates = [
    it?.card?.image_gallery,
    it?.card?.imageGallery,
    it?.card?.gallery,
    it?.card?.images,
    it?.card?.photos,
    mergedPayload.image_gallery,
    mergedPayload.imageGallery,
    mergedPayload.gallery,
    mergedPayload.images,
    mergedPayload.photos,
  ];

  for (const candidate of galleryCandidates) {
    const url = pickFirstImage(candidate);
    if (url) return url;
  }

  return undefined;
};

export const deriveCardType = (it: any) => {
  // First check explicit card type fields
  let type = (it?.card?.card_type || it?.type || '').toLowerCase();
  
  // If type is 'custom', treat it as unresolved and look into payload
  if (type === 'custom') {
    type = '';
  }
  
  // If not found, check in merged payload
  if (!type) {
    const mergedPayload = mergePayload(it);
    type = (mergedPayload.type || mergedPayload.card_type || '').toLowerCase();
  }
  
  if (!type) return 'poi';
  return type;
};

export const CARD_TYPE_META: Record<string, { label: string; accent: string; surface: string }> = {
  external: { label: '网页', accent: '#FB7A4A', surface: '#FFF5F0' },
  video: { label: '视频', accent: '#FB7A4A', surface: '#FFF5F0' },
  restaurant: { label: '餐饮', accent: '#EA580C', surface: '#FFF7ED' },
  poi: { label: '景点', accent: '#2563EB', surface: '#EFF6FF' },
  activity: { label: '活动', accent: '#0891B2', surface: '#ECFEFF' },
  shopping: { label: '购物', accent: '#0F766E', surface: '#ECFDF5' },
  lodging: { label: '住宿', accent: '#7C3AED', surface: '#F5F3FF' },
  transport: { label: '交通', accent: '#B45309', surface: '#FFFBEB' },
  default: { label: '行程', accent: '#374151', surface: '#F3F4F6' },
};

export const resolveCardTypeMeta = (type: string) => CARD_TYPE_META[type] || CARD_TYPE_META.default;

export const buildMetaPairs = (it: any, mergedPayload: Record<string, any>) => {
  const meta: Array<{ label: string; value: string }> = [];
  // Track labels we've already added so we don't duplicate when appending
  const seenLabels = new Set<string>();

  // If mergedPayload.metaPairs is present (e.g. copied from custom_payload),
  // prefer these entries as authoritative and include them first.
  if (Array.isArray(mergedPayload?.metaPairs)) {
    try {
      for (const p of mergedPayload.metaPairs) {
        if (!p || typeof p.label !== 'string') continue;
        const lbl = p.label.trim();
        if (!lbl) continue;
        const val = (p.value === null || typeof p.value === 'undefined') ? '' : String(p.value);
        meta.push({ label: lbl, value: val });
        seenLabels.add(lbl.toLowerCase());
      }
    } catch (e) {
      // defensive: ignore malformed metaPairs
    }
  }
  const type = deriveCardType(it);

  const duration = mergedPayload.recommendedDurationMinutes ?? mergedPayload.durationMins ?? mergedPayload.duration_minutes;
  const formattedDuration = formatDuration(typeof duration === 'number' ? duration : Number(duration));
  if (formattedDuration) meta.push({ label: '建议时长', value: formattedDuration });

  const priceRange = coalesceText(it?.card?.price_range, mergedPayload.price_range, mergedPayload.price);
  if (priceRange) meta.push({ label: '参考价位', value: priceRange });

  if (typeof it?.card?.rating === 'number' && it.card.rating > 0) {
    const reviewCount = typeof it?.card?.review_count === 'number' && it.card.review_count > 0
      ? ` · ${it.card.review_count} 条评价`
      : '';
    meta.push({ label: '评分', value: `${it.card.rating.toFixed(1)}${reviewCount}` });
  }

  const appendIfValue = (label: string, value: unknown) => {
    if (value === undefined || value === null || value === '') return;
    // avoid overwriting values provided explicitly in metaPairs
    if (seenLabels.has(String(label).toLowerCase())) return;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) meta.push({ label, value: trimmed });
      return;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      meta.push({ label, value: String(value) });
      return;
    }
    const formatted = formatList(value);
    if (formatted) meta.push({ label, value: formatted });
  };

  appendIfValue('地址', coalesceText(it?.card?.address, mergedPayload.address, mergedPayload.location, it?.card?.location));
  appendIfValue('电话', mergedPayload.phone || it?.card?.phone);
  appendIfValue('网站', mergedPayload.website || it?.card?.website);
  appendIfValue('邮箱', mergedPayload.email || it?.card?.email);
  appendIfValue('最佳季节', coalesceText(it?.card?.best_season, mergedPayload.best_season, mergedPayload.bestSeason));

  if (type === 'restaurant' || type === 'meal') {
    appendIfValue('餐厅', mergedPayload.restaurantName || mergedPayload.title);
    appendIfValue('菜系', mergedPayload.cuisine);
    // signatureDishes may be stored under several keys depending on source/enrichment.
    // Coalesce common variants so UI shows recommended dishes consistently.
    let recommendedDishes = mergedPayload.signatureDishes || mergedPayload.recommended || mergedPayload.recommendedDishes || mergedPayload.recommended_dishes || mergedPayload.signature_dishes;

    // Also consider card object top-level signatureDishes if present (some code writes there)
    if ((!recommendedDishes || (Array.isArray(recommendedDishes) && recommendedDishes.length === 0)) && it && it.card) {
      try {
        recommendedDishes = recommendedDishes || it.card.signatureDishes || it.card.signature_dishes || (it.card.base_payload && (it.card.base_payload.signatureDishes || it.card.base_payload.signature_dishes));
      } catch (e) {}
    }

    // If not present at top-level, also support legacy/newer metaPairs array form
    // where data is stored as [{ label: '推荐菜', value: 'xxx' }, ...]
    if ((!recommendedDishes || (Array.isArray(recommendedDishes) && recommendedDishes.length === 0)) && Array.isArray(mergedPayload.metaPairs)) {
      try {
        const pair = (mergedPayload.metaPairs as any[]).find((p) => {
          if (!p || typeof p.label !== 'string') return false;
          const label = p.label.trim().toLowerCase();
          // match common Chinese label and some English variants
          return label === '推荐菜' || label === '推荐菜：' || label === '推荐菜:' || label === 'signature dishes' || label.includes('推荐') || label.includes('recommended');
        });
        if (pair && pair.value) recommendedDishes = pair.value;
      } catch (e) {
        // defensive: if metaPairs has unexpected shapes, ignore
      }
    }

    // Normalize recommendedDishes to either string or array of strings split by common delimiters
    try {
      const normalizeSplit = (val: any): any => {
        if (!val && val !== 0) return undefined;
        if (Array.isArray(val)) {
          // flatten and split items that contain delimiters
          const parts: string[] = [];
          for (const item of val) {
            if (!item && item !== 0) continue;
            const s = String(item).trim();
            const segs = s.split(/[,，、;；\/\\]+/).map(x => x.trim()).filter(Boolean);
            parts.push(...segs);
          }
          return parts.length ? parts : undefined;
        }
        if (typeof val === 'string') {
          const s = val.trim();
          const segs = s.split(/[,，、;；\/\\]+/).map(x => x.trim()).filter(Boolean);
          return segs.length > 1 ? segs : s;
        }
        // fallback: try to stringify
        try { const s = JSON.stringify(val); return s; } catch (e) { return undefined; }
      };
      recommendedDishes = normalizeSplit(recommendedDishes);
    } catch (e) {
      // ignore normalization errors
    }
    appendIfValue('推荐菜', recommendedDishes);
    appendIfValue('用餐形式', mergedPayload.course);
    appendIfValue('座位', mergedPayload.seating || mergedPayload.capacity);
    appendIfValue('预订建议', mergedPayload.bookingTips);
    appendIfValue('营业时段', formatOpeningHours(mergedPayload.openingHours || mergedPayload.opening_hours || it?.card?.opening_hours));
  } else if (type === 'lodging') {
    appendIfValue('酒店', mergedPayload.hotelName || mergedPayload.title);
    appendIfValue('星级', mergedPayload.starRating);
    appendIfValue('入住', mergedPayload.checkIn || mergedPayload.check_in);
    appendIfValue('退房', mergedPayload.checkOut || mergedPayload.check_out);
    appendIfValue('设施', mergedPayload.amenities);
    appendIfValue('预订提示', mergedPayload.bookingTips);
  } else if (type === 'transport') {
    appendIfValue('交通方式', mergedPayload.mode || mergedPayload.transportMode);
    appendIfValue('出发', mergedPayload.from);
    appendIfValue('抵达', mergedPayload.to);
    appendIfValue('班次', mergedPayload.code);
    appendIfValue('座位/舱位', mergedPayload.cabin || mergedPayload.seatClass);
    appendIfValue('耗时', mergedPayload.durationText || mergedPayload.duration);
    appendIfValue('提示', mergedPayload.tips);
  } else if (type === 'external') {
    // External type (e.g. video embeds, external links): don't auto-generate metadata
    // Users should only see explicitly added metaPairs (phone, address, etc.)
    // Skip auto-adding 'placeName' and 'type' fields
  } else {
    // Default: poi/activity/place types
    appendIfValue('地点', mergedPayload.placeName || mergedPayload.title);
    appendIfValue('活动类型', mergedPayload.type);
    const ticket = mergedPayload.ticketInfo || mergedPayload.ticket_info;
    if (ticket) {
      if (ticket.isFree) meta.push({ label: '门票', value: '免费开放' });
      else appendIfValue('门票', ticket.reference);
    }
    appendIfValue('营业时段', formatOpeningHours(mergedPayload.openingHours || mergedPayload.opening_hours || it?.card?.opening_hours));
    appendIfValue('提示', mergedPayload.tips);
  }

  const lat = typeof mergedPayload.latitude === 'number' ? mergedPayload.latitude : (typeof it?.card?.latitude === 'number' ? it.card.latitude : undefined);
  const lng = typeof mergedPayload.longitude === 'number' ? mergedPayload.longitude : (typeof it?.card?.longitude === 'number' ? it.card.longitude : undefined);
  if (typeof lat === 'number' && typeof lng === 'number') {
    meta.push({ label: '坐标', value: `${lat.toFixed(4)}, ${lng.toFixed(4)}` });
  }

  return meta;
};

export const buildHighlightLines = (it: any, mergedPayload: Record<string, any>) => {
  const lines: string[] = [];
  const highlights = arrayify(mergedPayload.highlights || mergedPayload.features);
  if (highlights.length) lines.push(...highlights.map((item) => `亮点：${item}`));

  const popular = arrayify(mergedPayload.popularItems || mergedPayload.popular_items);
  if (popular.length) lines.push(`热门推荐：${popular.join('、')}`);

  const notes = arrayify(mergedPayload.notes);
  if (notes.length) lines.push(...notes.map((item) => `备注：${item}`));

  const safety = arrayify(mergedPayload.safetyTips || mergedPayload.safety_notes);
  if (safety.length) lines.push(...safety.map((item) => `安全提示：${item}`));

  const specialties = arrayify(mergedPayload.specialties);
  if (specialties.length) lines.push(`特色：${specialties.join('、')}`);

  const services = arrayify(mergedPayload.services);
  if (services.length) lines.push(`服务：${services.join('、')}`);

  return lines;
};

export const getTypeIcon = (cardType: string): string => {
  switch (cardType) {
    case 'external': return 'travel-explore';
    case 'video': return 'play-circle-filled';
    case 'poi':
    case 'place': return 'place';
    case 'restaurant': return 'restaurant';
    case 'hotel':
    case 'lodging': return 'hotel';
    case 'transport': return 'directions';
  case 'activity': return 'event';
    case 'shopping': return 'shopping-bag';
    default: return 'place';
  }
};

export const detectYouTubeEmbed = (url?: string | null) => {
  if (!url || typeof url !== 'string') return null;
  try {
    const u = url.trim();
    
    // Check domain first to avoid false positives on generic paths like /embed/
    // Matches youtube.com, www.youtube.com, m.youtube.com, youtu.be
    const isYoutubeDomain = /^(https?:\/\/)?(www\.|m\.)?(youtube\.com|youtu\.be)/i.test(u);
    
    if (!isYoutubeDomain) return null;

    // youtu.be/ID
    const short = u.match(/youtu\.be\/(?<id>[A-Za-z0-9_-]{6,})/i);
    if (short && short.groups && short.groups.id) {
      const id = short.groups.id;
      return { provider: 'youtube', id, src: `https://www.youtube.com/embed/${id}?playsinline=1&enablejsapi=1`, poster: `https://img.youtube.com/vi/${id}/hqdefault.jpg` };
    }
    // youtube.com/watch?v=ID or &v=ID
    const watch = u.match(/[?&]v=(?<id>[A-Za-z0-9_-]{6,})/i);
    if (watch && watch.groups && watch.groups.id) {
      const id = watch.groups.id;
      return { provider: 'youtube', id, src: `https://www.youtube.com/embed/${id}?playsinline=1&enablejsapi=1`, poster: `https://img.youtube.com/vi/${id}/hqdefault.jpg` };
    }
    // /embed/ID or /shorts/ID
    const embed = u.match(/\/(?:embed|shorts)\/(?<id>[A-Za-z0-9_-]{6,})/i);
    if (embed && embed.groups && embed.groups.id) {
      const id = embed.groups.id;
      return { provider: 'youtube', id, src: `https://www.youtube.com/embed/${id}?playsinline=1&enablejsapi=1`, poster: `https://img.youtube.com/vi/${id}/hqdefault.jpg` };
    }
  } catch (e) {
    // ignore parse errors
  }
  return null;
};
